import Vue from 'vue'
import relativeDate from 'relative-date'
import { firestoreCollection, firestoreDoc } from '../../util/firestoreUtil'
import { query, where, orderBy, startAfter, limit, getDoc, getDocs } from 'firebase/firestore'
import ShareLevel from '@/constants/Day6ShareLevel'

const firestoreSummaryDataConverter = {
  fromFirestore: (snapshot, options) => {
    const data = snapshot.data(options).exin
    if (data.dlt) { return { dlt: true } }

    const id = snapshot.id
    const {
      tsMod,
      name,
      notes,
      publicShareId,
      shareLookup
    } = data

    let shareLevel = ShareLevel.PRIVATE
    if (publicShareId) {
      shareLevel = ShareLevel.PUBLIC
    } else if (shareLookup && Object.keys(shareLookup).length) {
      shareLevel = ShareLevel.VERSIONS
    }

    const lastModifiedDate = tsMod.toDate()
    const result = {
      id,
      lastModifiedDate,
      lastModified: relativeDate(lastModifiedDate),
      name,
      shareLevel
    }
    if (notes) { result.notes = notes }
    if (publicShareId) { result.shareId = publicShareId }

    // View model
    result.isSelected = false

    return result
  }
}

const CharacterDataCache = class {
  get isLoading () {
    return this._isLoading
  }

  get pageData () {
    const pageData = []

    if (this.uid) {
      for (let i = 0; i < this._cacheOrder.length; i++) {
        const itemData = this._cache[this._cacheOrder[i]]
        itemData.index = i
        pageData.push(itemData)
      }
    }

    return pageData
  }

  async reset (uid, itemsPerPage, force = false) {
    if (uid === this.uid && !force) { return }

    this.uid = uid
    this.itemsPerPage = itemsPerPage
    this._cache = {}
    this._cacheOrder = []
    this._isQueriedAll = false
  }

  _cacheData (data) {
    let pushCount = 0

    const cache = this._cache
    for (const d of data) {
      const docId = d.id
      if (docId in cache) {
        Vue.logger.log(`Duplicate document encountered ${docId} at index ${this._cacheOrder.length}`)
      } else {
        cache[docId] = d
        this._cacheOrder.push(docId)
        pushCount++
      }
    }

    return pushCount
  }

  get _ignoreCall () {
    return !this.uid || this._isLoading
  }

  summaryDocPath (idSummary) {
    return `day6-data/${this.uid}/day6-account-dimension-summaries/${idSummary}`
  }

  async queryPage ({ idSummary, queryCount }) {
    const clauses = [
      where('uid', '==', this.uid),
      where('exin.dlt', '==', false),
      where('exin.hide', '==', false),
      orderBy('exin.tsMod', 'desc'),
      orderBy('exin.name')
    ]
    if (idSummary) {
      const relativeSnap = await getDoc(firestoreDoc(this.summaryDocPath(idSummary)))
      if (relativeSnap.exists) {
        clauses.push(startAfter(relativeSnap))
      }
    }
    clauses.push(limit(queryCount))

    const q = query(
      firestoreCollection(`day6-data/${this.uid}/day6-account-dimension-summaries`),
      ...clauses
    ).withConverter(firestoreSummaryDataConverter)
    const querySnapshot = await getDocs(q)

    return querySnapshot.docs.map(doc => doc.data())
  }

  async pageForward (force) {
    if (this._ignoreCall) { return }

    if (this._isQueriedAll && !force) { return }

    const itemsPerPage = this.itemsPerPage
    const cachedCount = this._cacheOrder.length

    this._isLoading = true
    const queryCount = itemsPerPage
    try {
      const results = await this.queryPage({
        idSummary: cachedCount > 0 ? this._cacheOrder[cachedCount - 1] : undefined,
        queryCount
      })

      const newCount = this._cacheData(results)
      this._isQueriedAll = results.length === 0 || newCount < queryCount
    } finally {
      this._isLoading = false
    }
  }

  // TODO What happens where backend errors due to missing dimensions (from delete elsewhere, corrupted, ...). Allow full refresh button to reset and re-query

  async reorderToFirst ({ id, index }) {
    const orderCopy = this._cacheOrder.slice()
    let newOrder = []
    if (index >= 0 && index < orderCopy.length && orderCopy[index] === id) {
      newOrder = orderCopy
      newOrder.splice(index, 1)
      newOrder.unshift(id)
    } else {
      for (let i = 0; i < orderCopy.length; i++) {
        const idAtIndex = orderCopy[i]
        if (idAtIndex === id) {
          newOrder.unshift(idAtIndex)
        } else {
          newOrder.push(idAtIndex)
        }
      }
    }
    // TODO Update last modified and related if changed in backend. Otherwise not important.
    this._cacheOrder = newOrder
  }

  async deleteData ({ id, index }) {
    const ordered = this._cacheOrder
    if (index >= 0 && index < ordered.length && ordered[index] === id) {
      ordered.splice(index, 1)
    } else {
      for (let i = 0; i < ordered.length; i++) {
        if (ordered[i] === id) {
          ordered.splice(i, 1)
          delete this._cacheData[id]
          break
        }
      }
    }
  }

  indexOf (idSummary) {
    let index = -1
    if (idSummary) {
      index = this._cache[idSummary].index
    }
    return index
  }

  async loadNewCharacter () {
    if (this._ignoreCall) { return }

    this._isLoading = true
    try {
      const results = await this.queryPage({ queryCount: 1 })
      const cache = this._cache
      for (const result of results) {
        const docId = result.id
        if (!(docId in cache)) {
          cache[docId] = result
          this._cacheOrder.unshift(docId)
          return result
        }
      }
    } finally {
      this._isLoading = false
    }
  }

  async refreshReorder (idSummary) {
    if (!idSummary) { return }

    const cache = this._cache
    const docRef = firestoreDoc(this.summaryDocPath(idSummary)).withConverter(firestoreSummaryDataConverter)
    const snap = await getDoc(docRef)
    if (!snap.exists) { return }

    const ordered = this._cacheOrder

    if (idSummary in cache) {
      cache[idSummary] = Object.assign(cache[idSummary], snap.data())

      const { index } = cache[idSummary]
      if (index < 0 || index >= ordered.length || ordered[index] !== idSummary) {
        Vue.logger.error(new Error(`Character refresh reorder out of order ${index} ${ordered.length}`))
        return false
      }

      ordered.splice(index, 1)
    } else {
      cache[idSummary] = snap.data()
    }

    ordered.unshift(idSummary)
    return true
  }
}

export default CharacterDataCache
